import { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, Clock, ChevronRight, Copy, Check } from 'lucide-react';
import type { GenerationVersionSummary } from '@shared/schemas/generation-version';

interface VersionPanelProps {
  versions: GenerationVersionSummary[];
  currentVersionId: string | null;
  selectedVersionId: string | null;
  onVersionSelect: (versionId: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  disabled?: boolean;
}

/**
 * Format timestamp to relative time or clock time
 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // If today, show clock time
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  // Otherwise show relative
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Unified Version Panel - Vertical timeline with visual selection indicator
 *
 * Features:
 * - Collapsible header with version count
 * - Vertical timeline with dots and connecting lines
 * - Visual indicator of selected version (user can edit from any version)
 * - Simplified UX: click a version to select it, then generate to create new version from it
 *
 * Visual Design:
 * ┌─────────────────────────────────┐
 * │  Version History          [-]  │
 * ├─────────────────────────────────┤
 * │  ●  v4 (current)    12:34 PM   │
 * │  │                              │
 * │  ○  v3              12:30 PM   │
 * │  │                              │
 * │  ◉  v2 ← selected   12:25 PM   │  ← Just visual indicator
 * │  │                              │
 * │  ○  v1              12:20 PM   │
 * └─────────────────────────────────┘
 */
export function VersionPanel({
  versions,
  currentVersionId,
  selectedVersionId,
  onVersionSelect,
  isCollapsed: controlledCollapsed,
  onToggleCollapse,
  disabled = false,
}: VersionPanelProps) {
  // Internal collapse state (used when not controlled)
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  // Track which version's prompt is expanded
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  // Track copied state for feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyPrompt = useCallback(async (e: React.MouseEvent, versionId: string, prompt: string) => {
    e.stopPropagation(); // Prevent version selection
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedId(versionId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleToggleExpand = useCallback((e: React.MouseEvent, versionId: string) => {
    e.stopPropagation(); // Prevent version selection
    setExpandedPromptId(prev => prev === versionId ? null : versionId);
  }, []);

  // Use controlled state if provided, otherwise internal
  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const toggleCollapse = onToggleCollapse ?? (() => setInternalCollapsed(!internalCollapsed));

  if (versions.length === 0) {
    return null;
  }

  // Sort versions by version number descending (newest first for display)
  const sortedVersions = [...versions].sort(
    (a, b) => b.versionNumber - a.versionNumber
  );

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={toggleCollapse}
        className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-slate-50"
        aria-expanded={!isCollapsed}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900">
            Version History
          </span>
          <span className="text-xs text-slate-400">
            {versions.length} {versions.length === 1 ? 'version' : 'versions'}
          </span>
        </div>
        {isCollapsed ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {/* Timeline Content */}
      {!isCollapsed && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="relative">
            {sortedVersions.map((version, index) => {
              const isCurrent = version.id === currentVersionId;
              const isSelected = version.id === selectedVersionId;
              const isLast = index === sortedVersions.length - 1;

              return (
                <div key={version.id} className="relative">
                  {/* Version Row */}
                  <button
                    type="button"
                    onClick={() => onVersionSelect(version.id)}
                    disabled={disabled}
                    className={`group relative flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                      isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                  >
                    {/* Dot and Line Container */}
                    <div className="relative flex flex-col items-center">
                      {/* Dot */}
                      <div
                        className={`relative z-10 h-3 w-3 shrink-0 rounded-full ${
                          isCurrent
                            ? 'bg-slate-900'
                            : isSelected
                              ? 'border-2 border-slate-900 bg-white'
                              : 'border border-slate-300 bg-white'
                        }`}
                      />
                      {/* Vertical Line (except for last item) */}
                      {!isLast && (
                        <div className="absolute left-1/2 top-3 h-8 w-px -translate-x-1/2 bg-slate-200" />
                      )}
                    </div>

                    {/* Version Info */}
                    <div className="min-w-0 flex-1 pt-px">
                      <div className="flex items-center gap-2">
                        {/* Version number */}
                        <span
                          className={`text-sm font-medium ${
                            isCurrent || isSelected
                              ? 'text-slate-900'
                              : 'text-slate-700'
                          }`}
                        >
                          v{version.versionNumber}
                        </span>

                        {/* Latest badge - only on most recent version */}
                        {isCurrent && !isSelected && (
                          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                            latest
                          </span>
                        )}

                        {/* Viewing indicator - when user is viewing this version */}
                        {isSelected && (
                          <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-white">
                            viewing
                          </span>
                        )}
                      </div>

                      {/* Prompt with expand/collapse */}
                      {(() => {
                        const promptText = version.editInstruction || version.prompt;
                        const isExpanded = expandedPromptId === version.id;

                        return (
                          <div className="mt-0.5">
                            {/* Truncated (single line, full width) or Full Prompt */}
                            {isExpanded ? (
                              <p className="text-xs text-slate-500 whitespace-pre-wrap break-words select-text">
                                {promptText}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500 truncate">
                                {promptText}
                              </p>
                            )}

                            {/* Expand/Copy Controls */}
                            <div className="mt-1 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(e) => handleToggleExpand(e, version.id)}
                                className="flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
                              >
                                <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                {isExpanded ? 'collapse' : 'expand'}
                              </button>
                              {isExpanded && (
                                <button
                                  type="button"
                                  onClick={(e) => handleCopyPrompt(e, version.id, promptText)}
                                  className="flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                  {copiedId === version.id ? (
                                    <>
                                      <Check className="h-3 w-3 text-green-500" />
                                      <span className="text-green-500">copied</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-3 w-3" />
                                      copy
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Timestamp */}
                    <div className="flex shrink-0 items-center gap-1 pt-px text-xs text-slate-400">
                      <Clock className="h-3 w-3" />
                      <span>{formatTime(version.createdAt)}</span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
